// The R3F canvas + all game systems assembled.
import { Suspense, useCallback, useEffect, useMemo, useRef } from "react";
import { Canvas } from "@react-three/fiber";
import * as THREE from "three";
import { useSettings } from "@/stores/settingsStore";
import { useMatchStore } from "@/stores/matchStore";
import { matchRefs } from "@/game/systems/matchRefs";
import Player from "@/game/Player";
import Weapon from "@/game/Weapon";
import Bots, { applyDamageToBot } from "@/game/Bots";
import TrainingYard, { TRAINING_YARD_META } from "@/game/maps/TrainingYard";
import Factory, { FACTORY_META } from "@/game/maps/Factory";
import Warehouse, { WAREHOUSE_META } from "@/game/maps/Warehouse";
import DesertBase, { DESERT_BASE_META } from "@/game/maps/DesertBase";

const MAP_REGISTRY = {
    training_yard: { meta: TRAINING_YARD_META, Component: TrainingYard },
    factory: { meta: FACTORY_META, Component: Factory },
    warehouse: { meta: WAREHOUSE_META, Component: Warehouse },
    desert_base: { meta: DESERT_BASE_META, Component: DesertBase },
};

export default function FPSGame({ config, onPause }) {
    const playerRef = useRef();
    const botsRef = useRef([]);
    const settings = useSettings();
    const entry = useMemo(
        () => MAP_REGISTRY[config.mapId] || MAP_REGISTRY.training_yard,
        [config.mapId]
    );
    const mapMeta = entry.meta;
    const MapScene = entry.Component;

    const onBotHit = useCallback((botId, dmg) => {
        applyDamageToBot(botsRef.current, botId, dmg);
    }, []);

    // Publish live refs for HUD overlays (minimap etc.) that live outside the R3F tree.
    useEffect(() => {
        matchRefs.player = playerRef.current;
        matchRefs.bots = botsRef.current;
        const t = setInterval(() => {
            matchRefs.player = playerRef.current;
            matchRefs.bots = botsRef.current;
        }, 200);
        return () => {
            clearInterval(t);
            matchRefs.player = null;
            matchRefs.bots = [];
        };
    }, []);

    const onAllEnemiesDead = useCallback(() => {
        useMatchStore.getState().endRound("won");
    }, []);

    const dpr = settings.quality === "low" ? [0.6, 1] : settings.quality === "medium" ? [0.8, 1.2] : [1, 1.75];

    return (
        <Canvas
            shadows={settings.shadows}
            camera={{ position: mapMeta.spawns.player.position, fov: settings.fov, near: 0.05, far: 400 }}
            gl={{
                antialias: settings.antialiasing,
                powerPreference: "high-performance",
                toneMapping: THREE.ACESFilmicToneMapping,
                toneMappingExposure: 1.05,
            }}
            dpr={dpr}
            frameloop="always"
        >
            <color attach="background" args={["#050710"]} />
            <Suspense fallback={null}>
                <MapScene
                    shadows={settings.shadows}
                    timeOfDay={config.timeOfDay || "day"}
                    weather={config.weather || "sunny"}
                />
                <Bots
                    teamSize={config.teamSize}
                    enemyDifficulty={config.enemyDifficulty || config.difficulty}
                    friendlyDifficulty={config.friendlyDifficulty || config.difficulty}
                    enemyCount={config.enemyCount}
                    friendlyCount={config.friendlyCount}
                    friendlyFire={!!config.friendlyFire}
                    mapMeta={mapMeta}
                    playerRef={playerRef}
                    botsRef={botsRef}
                    onAllEnemiesDead={onAllEnemiesDead}
                />
                <Weapon
                    playerRef={playerRef}
                    bots={botsRef.current}
                    walls={mapMeta.walls}
                    onBotHit={onBotHit}
                />
                <Player
                    ref={playerRef}
                    walls={mapMeta.walls}
                    spawn={mapMeta.spawns.player}
                    onPause={onPause}
                />
            </Suspense>
        </Canvas>
    );
}
